import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { siteConfig, legalDisclaimer } from '@/data/mock';

const Impressum = () => {
  return (
    <div className="min-h-screen pt-20 bg-[hsl(210,40%,98%)]">
      {/* Header */}
      <section className="py-12 bg-white border-b border-[hsl(210,25%,92%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="mb-6 text-[hsl(215,15%,45%)] hover:text-[hsl(213,80%,35%)]">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Startseite
            </Link>
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(215,25%,15%)]">
            Impressum & Datenschutz
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* Important Disclaimer */}
          <Card className="border-[hsl(35,70%,80%)] bg-[hsl(35,90%,98%)]">
            <CardContent className="p-6">
              <div className="flex items-start">
                <Shield className="w-6 h-6 text-[hsl(35,90%,40%)] mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-[hsl(35,50%,25%)] mb-2">{legalDisclaimer.title}</h3>
                  <p className="text-[hsl(35,40%,35%)]">{legalDisclaimer.content}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Impressum */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">Impressum</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Angaben gemäß § 5 ECG</h3>
                  <p className="text-[hsl(215,15%,45%)]">
                    Verein Väternotruf.com<br />
                    Rabenhof 16<br />
                    3525 Sallingberg<br />
                    Österreich
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Kontakt</h3>
                  <div className="space-y-2 text-[hsl(215,15%,45%)]">
                    <p className="flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      0660 233 1560
                    </p>
                    <p className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      kontakt@vaeternotruf.com
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Verantwortlich für den Inhalt</h3>
                  <p className="text-[hsl(215,15%,45%)]">
                    Verein Väternotruf.com<br />
                    Rabenhof 16<br />
                    3525 Sallingberg, Österreich
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">EU-Streitschlichtung</h3>
                  <p className="text-[hsl(215,15%,45%)]">
                    Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: 
                    <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[hsl(213,80%,35%)] hover:underline ml-1">
                      https://ec.europa.eu/consumers/odr
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datenschutz */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">Datenschutzerklärung</h2>
              
              <div className="space-y-6 text-[hsl(215,15%,45%)]">
                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">1. Datenschutz auf einen Blick</h3>
                  <p>
                    Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                    personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                    Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">2. Datenerfassung auf dieser Website</h3>
                  <p className="mb-3">
                    <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                    Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                    Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
                  </p>
                  <p>
                    <strong>Wie erfassen wir Ihre Daten?</strong><br />
                    Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
                    Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">3. Ihre Rechte</h3>
                  <p>
                    Sie haben jederzeit das Recht unentgeltlich Auskunft über Herkunft, Empfänger und 
                    Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem 
                    ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Hierzu sowie 
                    zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns wenden.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">4. Kontaktformular</h3>
                  <p>
                    Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben 
                    aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten 
                    zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns 
                    gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">5. Terminbuchung</h3>
                  <p>
                    Bei der Buchung eines Termins werden Ihre eingegebenen Daten (Name, E-Mail, Telefon) 
                    gespeichert, um den Termin zu verwalten und Sie kontaktieren zu können. Die Daten 
                    werden nach Abschluss des Beratungsgesprächs gelöscht, sofern keine gesetzlichen 
                    Aufbewahrungspflichten bestehen.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">6. SSL-Verschlüsselung</h3>
                  <p>
                    Diese Seite nutzt aus Sicherheitsgründen eine SSL-Verschlüsselung. Eine 
                    verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers 
                    von "http://" auf "https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Haftungsausschluss */}
          <Card className="border-[hsl(210,25%,90%)]">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">Haftungsausschluss</h2>
              
              <div className="space-y-6 text-[hsl(215,15%,45%)]">
                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Keine Rechtsberatung</h3>
                  <p>
                    Die auf dieser Website angebotenen Dienstleistungen stellen ausdrücklich <strong>keine 
                    Rechtsberatung</strong> dar. Der Betreiber dieser Website ist kein Rechtsanwalt und 
                    nicht zur Rechtsberatung befugt. Alle Informationen und Empfehlungen basieren auf 
                    persönlicher Erfahrung und ersetzen nicht die Beratung durch einen zugelassenen 
                    Rechtsanwalt.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Haftung für Inhalte</h3>
                  <p>
                    Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, 
                    Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-2">Haftung für Links</h3>
                  <p>
                    Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir 
                    keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine 
                    Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige 
                    Anbieter oder Betreiber der Seiten verantwortlich.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Impressum;
